// @givmo/social — the module's pages, one per view (SMM-WEB1). The host mounts this beneath its
// `ModuleGate` (which decides absent / off / setting up / locked first), on the route that names the
// view, and hands it the organization and the person's permissions. No page branches on what kind
// of organization it serves.

import { useState, type ReactNode } from 'react';
import { type SocialPostOut } from '@givmo/api-client';
import { Button, Card, ErrorMessage, testIdProp } from '@givmo/design-system';
import { AccountsPage } from './accounts-page';
import { ComposerPage, NO_ENTRY, readComposerEntry, type ComposerEntry } from './composer-page';
import { ConnectReturnPage } from './connect-return-page';
import {
  EditScheduledDialog,
  VariantRow,
  actionErrorWords,
  editableWhenScheduled,
  postWhen,
  useCancelPost,
} from './post-parts';
import { PostsPage, readContextFilter } from './posts-page';
import { canPublish, canRead, type SocialConfig } from './social-config';
import { knownTimeZone } from './social-platforms';
import { usePost } from './social-queries';
import { readRescheduleTime } from './social-reschedule';
import { SOCIAL_PAGE_TEST_ID, SOCIAL_ROUTES, type SocialView } from './social-routes';
import { SocialLoading, SocialPageShell, SocialRefused, SocialStateCard } from './social-ui';
import { socialWords as w } from './social-words';

function currentSearch(): string {
  return globalThis.location?.search ?? '';
}

/** One view as its host frames it. */
export interface SocialFrameView {
  title: string;
  width?: 'wide' | 'narrow';
  /** The view's acts: in the page's header; below the view in a renter's embed. */
  actions?: ReactNode;
  children: ReactNode;
}

/** What wraps a view: the page's shell (its header, "Back to posts" and width), or, in a renter's
 *  embed (`composer-embed.tsx`), the view alone with its acts below it. */
export type SocialFrame = (view: SocialFrameView) => ReactNode;

/** Social's own page frame. */
export const pageFrame: SocialFrame = ({ title, width, actions, children }) => (
  <SocialPageShell
    title={title}
    testId={SOCIAL_PAGE_TEST_ID}
    width={width}
    back={{ to: SOCIAL_ROUTES.posts, label: w.backToPosts }}
    actions={actions}
  >
    {children}
  </SocialPageShell>
);

export function SocialPage({
  config,
  view = { page: 'posts' },
  entry,
}: {
  config: SocialConfig;
  view?: SocialView;
  /** The Email Campaign entry, by embed; a deep link's is read from the address. */
  entry?: ComposerEntry;
}) {
  const [search] = useState(currentSearch);
  if (!canRead(config)) {
    return (
      <SocialPageShell title={w.moduleName} testId="social-locked">
        <SocialStateCard
          testId="social-no-access"
          title={w.noAccessTitle}
          description={w.noAccessBody}
        />
      </SocialPageShell>
    );
  }
  switch (view.page) {
    case 'posts':
      return <PostsPage config={config} context={readContextFilter(search)} />;
    case 'compose':
      return canPublish(config) ? (
        <ComposerPage config={config} entry={entry ?? readComposerEntry(search)} />
      ) : (
        <PostsPage config={config} />
      );
    case 'post':
      return (
        <PostView
          config={config}
          postId={view.postId}
          composer={(post, keptTime) => (
            <ComposerPage
              key={post.post_id}
              config={config}
              post={post}
              entry={{ ...NO_ENTRY, suggestedAt: keptTime }}
            />
          )}
        />
      );
    case 'accounts':
      return <AccountsPage config={config} />;
    case 'connected':
      return <ConnectReturnPage config={config} />;
  }
}

/**
 * A post by its id: the composer while it is a draft this person may edit, else its read-only view
 * with the acts on it. The page frames each in its shell; a renter's embed shows them bare
 * (`composer-embed.tsx`), the same views either way.
 */
export function PostView({
  config,
  postId,
  composer,
  frame = pageFrame,
  compact = false,
  onEdited,
}: {
  config: SocialConfig;
  postId: string;
  /** The composer on an editable draft, given the time the post was scheduled for before it was
   *  opened for editing (its Schedule starts there). */
  composer: (post: SocialPostOut, keptTime: string | null) => ReactNode;
  frame?: SocialFrame;
  /** A renter's narrower frame: the post's image is held lower (true aspect, contained), so the
   *  post's acts stay on the first screen. Social's own page keeps its own. */
  compact?: boolean;
  /** In place of opening the post's address once a scheduled post is a draft again. */
  onEdited?: () => void;
}) {
  const post = usePost(config.orgRef, postId);
  if (config.orgRef === null || post.isPending) {
    return frame({ title: w.postTitle, children: <SocialLoading>{w.loadingPost}</SocialLoading> });
  }
  if (post.isError) {
    return frame({
      title: w.postTitle,
      children: (
        <SocialRefused
          error={post.error}
          onRetry={() => void post.refetch()}
          gone={{ title: w.postGoneTitle, description: w.postGoneBody }}
        />
      ),
    });
  }
  if (post.data.editable && canPublish(config)) {
    // A scheduled post opened for editing starts its Schedule from the time it had.
    return composer(post.data, readRescheduleTime(post.data.post_id));
  }
  return (
    <PostDetail
      orgRef={config.orgRef}
      post={post.data}
      canAct={canPublish(config)}
      frame={frame}
      compact={compact}
      zone={knownTimeZone(config.timeZone)}
      onEdited={onEdited}
    />
  );
}

/** A post that has gone (or is going) out, or a draft a reader cannot edit: read-only, with each
 *  account's state and the acts on it. */
function PostDetail({
  orgRef,
  post,
  canAct,
  frame,
  compact,
  zone,
  onEdited,
}: {
  orgRef: string;
  post: SocialPostOut;
  canAct: boolean;
  frame: SocialFrame;
  compact: boolean;
  /** A renter's organization's clock for the post's times (C1); null on Social's own page. */
  zone: string | null;
  onEdited?: () => void;
}) {
  const cancel = useCancelPost(orgRef, post);
  const canCancel = canAct && post.variants.some((variant) => variant.can_cancel);
  const canEditScheduled = canAct && editableWhenScheduled(post);
  const [editingScheduled, setEditingScheduled] = useState(false);
  return frame({
    title: w.postTitle,
    width: 'narrow',
    actions: canCancel ? (
      <div className="flex items-center gap-8">
        <Button
          appearance="saas"
          tone="ghost"
          loading={cancel.isPending}
          onClick={() => cancel.mutate()}
          testId="social-post-cancel"
        >
          {w.cancelScheduled}
        </Button>
        {canEditScheduled ? (
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => setEditingScheduled(true)}
            testId="social-post-edit-scheduled"
          >
            {w.editScheduled}
          </Button>
        ) : null}
      </div>
    ) : undefined,
    children: (
      <>
        <PostDetailCard
          orgRef={orgRef}
          post={post}
          canAct={canAct}
          cancel={cancel}
          compact={compact}
          zone={zone}
        />
        {editingScheduled ? (
          <EditScheduledDialog
            orgRef={orgRef}
            post={post}
            onClose={() => setEditingScheduled(false)}
            onEdited={onEdited}
          />
        ) : null}
      </>
    ),
  });
}

/** The post itself, read-only: its words, link and image, its time and author, and each account's
 *  state with the acts on it. */
function PostDetailCard({
  orgRef,
  post,
  canAct,
  cancel,
  compact,
  zone,
}: {
  orgRef: string;
  post: SocialPostOut;
  canAct: boolean;
  cancel: ReturnType<typeof useCancelPost>;
  compact: boolean;
  zone: string | null;
}) {
  return (
    <Card className="flex flex-col gap-14 px-24 py-20" testId="social-post-detail">
      <p className="whitespace-pre-line break-words font-sans text-s14 tracking-givmo text-text-strong">
        {post.text}
      </p>
      {post.link_url ? (
        <a
          href={post.link_url}
          target="_blank"
          rel="noreferrer"
          className="w-fit break-all font-sans text-s13 text-brand-600 underline-offset-2 hover:underline"
        >
          {post.link_url}
        </a>
      ) : null}
      {post.image ? (
        <img
          src={post.image.url}
          alt=""
          className={
            compact
              ? 'h-auto max-h-[200px] w-auto max-w-full self-start rounded-md border border-border-subtle object-contain'
              : 'max-h-[320px] w-fit max-w-full rounded-md border border-border-subtle'
          }
          {...testIdProp('social-post-image')}
        />
      ) : null}
      <p
        className="font-sans text-s12 tracking-givmo text-text-secondary"
        {...testIdProp('social-post-when')}
      >
        {postWhen(post, zone)}
        {post.author_name ? ` · ${w.by(post.author_name)}` : ''}
      </p>
      {cancel.isError ? (
        <ErrorMessage testId="social-post-cancel-error">
          {actionErrorWords(cancel.error)}
        </ErrorMessage>
      ) : null}
      <ul className="flex flex-col border-t border-border-subtle pt-4">
        {post.variants.map((variant) => (
          <VariantRow
            key={variant.variant_id}
            orgRef={orgRef}
            variant={variant}
            canAct={canAct}
            zone={zone}
          />
        ))}
      </ul>
    </Card>
  );
}
